import _slicedToArray from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { utils } from '@cofit/api';
import * as R from 'ramda';
import { MediaType } from 'src/constants/messageEvent';
import { Gender } from 'src/types';
import { OrderType } from 'src/types/sorting';
/**
 * Updates or adds a query parameter to a URL string
 *
 * @param {string | undefined} url - The URL to modify
 * @param {string} key - The query parameter name
 * @param {string} value - The query parameter value
 * @returns {string} - The updated URL string
 *
 * @example
 * const updatedUrl = changeQueryParamInUrlString(
 *   'https://example.com?page=1',
 *   'limit',
 *   '10'
 * );
 * // Returns: 'https://example.com?page=1&limit=10'
 */

export var changeQueryParamInUrlString = function changeQueryParamInUrlString(url, key, value) {
  if (!url) return '';
  var urlObj = new URL(url);
  urlObj.searchParams.set(key, value);
  return urlObj.toString();
};
/**
 * Creates a sorting function based on a property and sort order
 *
 * @param {string} sortingProp - The property name to sort by
 * @param {OrderType} orderType - The sort direction (asc or desc)
 * @returns {Function} - A function that sorts an array by the specified property
 *
 * @example
 * const users = [
 *   { name: 'Bob', age: 30 },
 *   { name: 'Alice', age: 25 }
 * ];
 * const sortedByName = sortByProp('name', OrderType.asc)(users);
 * // Returns: [{ name: 'Alice', age: 25 }, { name: 'Bob', age: 30 }]
 */

export var sortByProp = function sortByProp(sortingProp, orderType) {
  return R.sortWith([orderType === OrderType.desc ? R.descend(R.prop(sortingProp)) : R.ascend(R.prop(sortingProp))]);
};
/**
 * Safely parses a JSON string into an object
 *
 * @param {string} data - The JSON string to parse
 * @returns {Object} - The parsed object or an empty object if parsing fails
 *
 * @example
 * const obj = parseJson('{"name":"John","age":30}');
 * // Returns: { name: 'John', age: 30 }
 *
 * const invalidObj = parseJson('invalid json');
 * // Returns: {}
 */

export var parseJson = function parseJson(data) {
  var result = {};

  try {
    result = JSON.parse(data);
  } catch (e) {
    console.warn('Error parsing JSON', e);
  }

  return result;
};
/**
 * Creates a template function to replace <%= property %> pattern with object values
 *
 * @param {string} templateString - String template with <%= property %> pattern
 * @returns {Function} - Function that takes an object and returns a string with the template replaced
 *
 * @example
 * const greet = template('Hello <%= name %>!');
 * const greeting = greet({ name: 'John' });
 * // Returns: 'Hello John!'
 *
 * const complexTemplate = template('<%= greeting %>, <%= name %>. You are <%= age %> years old.');
 * const message = complexTemplate({ greeting: 'Hi', name: 'Alice', age: '30' });
 * // Returns: 'Hi, Alice. You are 30 years old.'
 */

export function template(templateString) {
  // Create a regex to match <%= property %> pattern
  var templateRegex = /<%=\s*([^%>]+)\s*%>/g;
  return function () {
    var param = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    // Replace each matched pattern with the corresponding value from data object
    return templateString.replace(templateRegex, function (_, key) {
      // Trim the key to remove any whitespace
      var pattern = key.trim(); // Return the value from data object or empty string if not found

      return param[pattern] != null ? param[pattern] : '';
    });
  };
}
/**
 * Maps select options and translates their labels
 *
 * @param {TFunction} t - i18next translation function
 * @returns {Function} - Function that maps and translates option labels
 *
 * @example
 * const options = [
 *   { value: 'en', label: 'English', i18nKey: 'language.english' }
 * ];
 * const { t } = useTranslation();
 * const translatedOptions = options.map(mapSelectOption(t));
 * // Returns: [{ value: 'en', label: '英文', i18nKey: 'language.english' }]
 */

export var mapSelectOption = function mapSelectOption(t) {
  return function (option) {
    return _objectSpread(_objectSpread({}, option), {}, {
      label: t(option.i18nKey, option.label)
    });
  };
};
/**
 * Checks if the media type is a video format
 *
 * @param {MediaType} mediaType - The media type to check
 * @returns {boolean} - True if media type is video
 *
 * @example
 * const isVideoMedia = isVideo(MediaType.video);
 * // Returns: true
 *
 * const isImageMedia = isVideo(MediaType.image);
 * // Returns: false
 */

export var isVideo = function isVideo(mediaType) {
  return mediaType === MediaType.video || mediaType === MediaType.quicktime;
};
/**
 * Calculates the best weight based on height and base weight constant
 *
 * @param {number} bmi - Target BMI
 * @param {number} currentHeight - Current height in centimeters
 * @returns {string|number} - Calculated weight or 0 if height over 100
 *
 * @example
 * const weight = getBestWeight(22, 180);
 * // Returns: '71.28'
 *
 * const invalidWeight = getBestWeight(22, 50);
 * // Returns: 0
 */

export var getBestWeight = function getBestWeight(bmi, currentHeight) {
  var canCalculate = currentHeight > 100;
  return canCalculate ? (Math.pow(currentHeight / 100, 2) * bmi).toFixed(2) : 0;
};
/**
 * Calculates estimated body fat mass percentage based on gender, BMI, and age
 *
 * @param {Object} params - The parameters for calculation
 * @param {Gender} params.gender - The gender (male or female)
 * @param {number} params.bmi - Body Mass Index value
 * @param {number} params.yearsOld - Age in years
 * @returns {string|number} - Calculated body fat percentage or empty string if parameters are missing
 *
 * @example
 * const bodyFat = getEstimatedBodyFatMassPercentage({
 *   gender: Gender.male,
 *   bmi: 25,
 *   yearsOld: 30
 * });
 * // Returns: 23.9
 */

export var getEstimatedBodyFatMassPercentage = function getEstimatedBodyFatMassPercentage(_ref) {
  var gender = _ref.gender,
      bmi = _ref.bmi,
      yearsOld = _ref.yearsOld;

  if (!bmi || !gender || !yearsOld) {
    return '';
  }

  return utils.Util.round(1.2 * bmi + 0.23 * yearsOld - 5.4 - 10.8 * (gender === Gender.male ? 1 : 0), 1);
};
/**
 * Generates a random integer from 0 to max-1
 *
 * @param {number} max - The upper bound (exclusive)
 * @returns {number} - Random integer between 0 and max-1
 *
 * @example
 * const randomValue = getRandomInt(10);
 * // Returns: a random number between 0 and 9
 */

export var getRandomInt = function getRandomInt(max) {
  return Math.floor(Math.random() * max);
};
/**
 * Return a new reordered array by moving an item from one position to another
 *
 * @param {Array} list - The array to reorder
 * @param {number} startIndex - The original position of the item
 * @param {number} endIndex - The destination position
 * @returns {Array} - New array with the item moved
 *
 * @example
 * const array = ['a', 'b', 'c', 'd'];
 * const reordered = reorderArray(array, 0, 2);
 * // Returns: ['b', 'c', 'a', 'd']
 */

export var reorderArray = function reorderArray(list, startIndex, endIndex) {
  var result = Array.from(list);

  var _result$splice = result.splice(startIndex, 1),
      _result$splice2 = _slicedToArray(_result$splice, 1),
      removed = _result$splice2[0];

  result.splice(endIndex, 0, removed);
  return result;
};
/**
 * Sorts an array of strings or objects with string properties using locale comparison
 *
 * @param {Array} data - The array to sort
 * @param {string} [key] - Optional property name to sort by (if sorting objects)
 * @param {boolean} [asc=true] - Sort in ascending order if true, descending if false
 * @returns {Array} - Sorted array
 *
 * @example
 * // Sort array of strings
 * const names = ['Žofia', 'Adam', 'Ørjan'];
 * const sortedNames = sortByLocale(names);
 * // Returns: ['Adam', 'Ørjan', 'Žofia']
 *
 * // Sort array of objects by property
 * const users = [{ name: 'Žofia' }, { name: 'Adam' }];
 * const sortedUsers = sortByLocale(users, 'name');
 * // Returns: [{ name: 'Adam' }, { name: 'Žofia' }]
 *
 * // Sort in descending order
 * const descendingNames = sortByLocale(names, undefined, false);
 * // Returns: ['Žofia', 'Ørjan', 'Adam']
 */

export var sortByLocale = function sortByLocale(data, key) {
  var asc = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  return R.sort(function (a, b) {
    var aVal = key ? a[key] : a;
    var bVal = key ? b[key] : b;
    return asc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  }, data);
};
/**
 * Extracts unique values for a specific property from an array of objects
 *
 * @param {Array} data - Array of objects
 * @param {string} key - Property name to extract
 * @returns {Array} - Array of unique non-empty values
 *
 * @example
 * const users = [
 *   { role: 'admin', name: 'John' },
 *   { role: 'user', name: 'Alice' },
 *   { role: 'admin', name: 'Bob' },
 *   { role: '', name: 'Guest' }
 * ];
 * const uniqueRoles = pluckDistinctValues(users, 'role');
 * // Returns: ['admin', 'user']
 */

export var pluckDistinctValues = function pluckDistinctValues(data, key) {
  return R.pipe(R.pluck(key), R.filter(R.complement(R.isEmpty)), R.uniq)(data);
};
/**
 * Rounds a number up to the nearest multiple of the specified interval
 *
 * @param {number} interval - The interval to round to
 * @param {number} value - The value to round
 * @returns {number} - Rounded value
 *
 * @example
 * const roundedUp = ceilBy(5, 12);
 * // Returns: 15
 */

export var ceilBy = function ceilBy(interval, value) {
  return Math.ceil(value / interval) * interval;
};
/**
 * Rounds a number down to the nearest multiple of the specified interval
 *
 * @param {number} interval - The interval to round to
 * @param {number} value - The value to round
 * @returns {number} - Rounded value
 *
 * @example
 * const roundedDown = floorBy(5, 12);
 * // Returns: 10
 */

export var floorBy = function floorBy(interval, value) {
  return Math.floor(value / interval) * interval;
};
/**
 * Rounds a number to a specified number of decimal places
 *
 * @param {number} precision - Number of decimal places
 * @param {number} value - The value to round
 * @returns {number} - Rounded value
 *
 * @example
 * const rounded = roundTo(2, 3.14159);
 * // Returns: 3.14
 */

export var roundTo = function roundTo(precision, value) {
  return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
};
/**
 * Linearly interpolates between two numbers based on a ratio
 *
 * @param {number} start - Starting value
 * @param {number} end - Ending value
 * @param {number} ratio - Interpolation ratio (0-1)
 * @returns {number} - Interpolated value
 *
 * @example
 * const mid = interpolate(0, 10, 0.5);
 * // Returns: 5
 *
 * const quarter = interpolate(0, 100, 0.25);
 * // Returns: 25
 */

export var interpolate = function interpolate(start, end, ratio) {
  return start + (end - start) * ratio;
};
/**
 * Capitalizes the first letter of a given string
 *
 * @param {string} str - The string to capitalize
 * @returns {string} - Capitalized string
 *
 * @example
 * const capitalized = capitalize('hello'); // 'Hello'
 */

export var capitalize = function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
export var formatPercentage = function formatPercentage(value) {
  var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return R.isNil(value) ? '' : "".concat((value * 100).toFixed(precision), "%");
};
export var getDelta = function getDelta(value, baseValue) {
  return R.isNil(value) || R.isNil(baseValue) ? undefined : value - baseValue;
};
export var getDeltaPercentage = function getDeltaPercentage(value, baseValue) {
  var delta = getDelta(value, baseValue);
  return R.isNil(delta) || Number(baseValue) === 0 ? undefined : delta / baseValue;
};
export var findLastPropertyValue = function findLastPropertyValue(data, key) {
  var found = R.findLast(function (val) {
    return R.isNotNil(R.prop(key, val));
  }, data);
  return found ? R.prop(key, found) : undefined;
};
export var findLastPathValue = function findLastPathValue(data, path) {
  var found = R.findLast(function (val) {
    return R.isNotNil(R.path(path, val));
  }, data);
  return found ? R.path(path, found) : undefined;
};
export var renderNumeric = function renderNumeric() {
  var precision = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;
  return function (cell) {
    var value = cell.getValue();

    if (R.isNil(value) || isNaN(value)) {
      return '';
    }

    return Number(value).toFixed(precision);
  };
};